export interface ZidOAuthCallbackDraft {
  code: string | null
  state: string | null
  error: string | null
  errorDescription: string | null
  capturedAt: number
}

export type ZidOAuthCallbackParams = Omit<ZidOAuthCallbackDraft, 'capturedAt'>

const STORAGE_KEY = 'tlaaq_zid_oauth_callback'

const EMPTY_CALLBACK: ZidOAuthCallbackParams = {
  code: null,
  state: null,
  error: null,
  errorDescription: null,
}

function canUseSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function normalizeDraft(value: unknown): ZidOAuthCallbackDraft | null {
  if (!isRecord(value)) return null
  const capturedAt = typeof value.capturedAt === 'number' ? value.capturedAt : Date.now()
  const draft: ZidOAuthCallbackDraft = {
    code: typeof value.code === 'string' ? value.code : null,
    state: typeof value.state === 'string' ? value.state : null,
    error: typeof value.error === 'string' ? value.error : null,
    errorDescription: typeof value.errorDescription === 'string' ? value.errorDescription : null,
    capturedAt,
  }
  return hasCallbackParams(draft) ? draft : null
}

function paramsFromUrl(url: URL): ZidOAuthCallbackParams {
  return {
    code: url.searchParams.get('code'),
    state: url.searchParams.get('state'),
    error: url.searchParams.get('error'),
    errorDescription: url.searchParams.get('error_description'),
  }
}

function isZidOAuthUrl(url: URL): boolean {
  const normalizedPath = url.pathname.replace(/\/+$/, '') || '/'
  return normalizedPath === '/zid-oauth'
}

export function hasCallbackParams(params: ZidOAuthCallbackParams): boolean {
  return Boolean(params.code || params.state || params.error || params.errorDescription)
}

export function saveZidOAuthCallback(params: ZidOAuthCallbackParams): ZidOAuthCallbackDraft | null {
  if (!hasCallbackParams(params)) return null
  const draft: ZidOAuthCallbackDraft = { ...params, capturedAt: Date.now() }
  if (!canUseSessionStorage()) return draft
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft))
  } catch {
    return draft
  }
  return draft
}

export function captureZidOAuthCallbackFromUrl(href: string = typeof window === 'undefined' ? '' : window.location.href): ZidOAuthCallbackDraft | null {
  if (!href) return null
  const url = new URL(href)
  if (!isZidOAuthUrl(url)) return null
  return saveZidOAuthCallback(paramsFromUrl(url))
}

export function readStoredZidOAuthCallback(): ZidOAuthCallbackDraft | null {
  if (!canUseSessionStorage()) return null
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return normalizeDraft(JSON.parse(raw))
  } catch {
    return null
  }
}

export function hasStoredZidOAuthCallback(): boolean {
  return readStoredZidOAuthCallback() !== null
}

export function readZidOAuthCallbackParams(): ZidOAuthCallbackParams {
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href)
    const fromUrl = paramsFromUrl(url)
    if (isZidOAuthUrl(url) && hasCallbackParams(fromUrl)) {
      saveZidOAuthCallback(fromUrl)
      return fromUrl
    }
  }

  const stored = readStoredZidOAuthCallback()
  if (stored) {
    return {
      code: stored.code,
      state: stored.state,
      error: stored.error,
      errorDescription: stored.errorDescription,
    }
  }

  return EMPTY_CALLBACK
}

export function clearZidOAuthCallback(): void {
  if (!canUseSessionStorage()) return
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore storage failures; the callback URL is stripped after success too.
  }
}
